import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"

import { SEO } from "@/components/SEO"
import { CloudInfrastructureSchema } from "@/components/schema"
import { PageFAQ } from "@/components/PageFAQ"
import { cloudInfrastructureFAQs } from "@/data/faqData"

const challenges = [
    {
        num: "01",
        stat: "68%",
        statLabel: "of cloud migrations exceed budget and timeline",
        title: "Legacy Lock-In",
        description: "Rigid on-prem and VM-centric environments limit agility, slow innovation, and create vendor dependencies that are expensive to unwind.",
        painPoints: ["Vendor lock-in", "Monolithic apps", "Technical debt"],
    },
    {
        num: "02",
        stat: "3.5x",
        statLabel: "more complexity in multi-cloud environments",
        title: "Operational Fragmentation",
        description: "Hybrid and multi-cloud architectures increase fragmentation, reduce visibility, and make it impossible to enforce consistent governance.",
        painPoints: ["Siloed tooling", "Visibility gaps", "Compliance drift"],
    },
    {
        num: "03",
        stat: "45%",
        statLabel: "of cloud spend is wasted or unoptimized",
        title: "Cost & Performance Drift",
        description: "Reactive scaling, manual provisioning, and lack of FinOps discipline lead to unpredictable costs and degraded performance.",
        painPoints: ["Over-provisioned", "No FinOps", "Cost surprises"],
    },
]

const approachSteps = [
    {
        num: "01",
        title: "Cloud Architecture Modernization",
        description: "Designing scalable, fault-tolerant architectures across AWS, Azure, and GCP with cloud-native patterns and best practices.",
    },
    {
        num: "02",
        title: "Hybrid & Multi-Cloud Strategy",
        description: "Seamless integration of cloud, edge, and on-prem environments with unified governance and consistent security posture.",
    },
    {
        num: "03",
        title: "Infrastructure Intelligence",
        description: "Applying automation, IaC, and predictive insights to optimize performance, reliability, and cost efficiency at scale.",
    },
]

const deliverableRows = [
    [
        "Cloud Architecture & Migration",
        "Kubernetes & Container Platforms",
        "Infrastructure as Code",
        "Cloud Security & Identity",
        "Observability & Monitoring",
        "Cost Optimization & FinOps",
        "Multi-Cloud Networking",
        "Data Platform Engineering",
    ],
    [
        "Serverless Architecture",
        "Edge Computing",
        "Cloud-Native Development",
        "Disaster Recovery & HA",
        "CI/CD Pipeline Design",
        "Service Mesh & API Gateway",
        "Database Migration",
        "Compliance Automation",
    ],
    [
        "DevOps Transformation",
        "Cloud Landing Zones",
        "GitOps & Platform Engineering",
        "Identity & Access Management",
        "Secrets Management",
        "Performance Engineering",
        "Capacity Planning",
        "Cloud Governance Frameworks",
    ],
]

const CloudInfrastructure = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Cloud & Infrastructure Built for Intelligent Scale | RSA Tech Group"
                description="We design resilient, cloud-native foundations that enable performance, security, and continuous growth across complex enterprise environments."
                image="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?q=80&w=2670&auto=format&fit=crop"
            />
            <CloudInfrastructureSchema />
            <Navigation />

            {/* Hero Section */}
            <div className="relative pt-48 pb-24 overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_30%,rgba(28,43,110,0.3),transparent_60%)]" />
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#1C2B6E]/10 rounded-full blur-[150px]" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FDA400]/[0.04] rounded-full blur-[120px]" />

                <div className="section-container relative z-10 text-center">
                    <div className="max-w-4xl mx-auto">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                            Cloud & Infrastructure
                        </p>
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-[1.1] tracking-tight">
                            Cloud & Infrastructure Built for{" "}
                            <span className="text-[#FDA400]">Intelligent Scale</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-12 font-light leading-relaxed">
                            We design resilient, cloud-native foundations that enable performance, security, and continuous growth across complex enterprise environments.
                        </p>
                        <Link
                            to="/contact"
                            className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-[#1C2B6E] bg-[#FDA400] rounded-full hover:bg-[#f5b800] transition-all duration-300 shadow-[0_8px_30px_rgba(253,164,0,0.3)] hover:shadow-[0_12px_40px_rgba(253,164,0,0.4)] hover:-translate-y-0.5"
                        >
                            Talk to a Cloud Infrastructure Expert <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                    </div>
                </div>
            </div>

            {/* The Challenge */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.2]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
                <div className="absolute bottom-0 right-1/3 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-20">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            The Problem
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            The Infrastructure{" "}
                            <span className="text-[#FDA400]">Challenges</span>{" "}
                            Enterprises Face
                        </h2>
                        <p className="text-slate-500 text-lg mt-6 max-w-3xl mx-auto font-light leading-relaxed">
                            Most enterprises struggle to modernize infrastructure without disrupting the business they depend on.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
                        {challenges.map((item) => (
                            <div
                                key={item.num}
                                className="partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.12)] transition-all duration-500 hover:-translate-y-2"
                            >
                                <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
                                    <div className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shine_0.8s_ease-in-out]" />
                                </div>
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />
                                <div className="absolute -top-16 -right-16 w-44 h-44 bg-[#FDA400]/[0.04] rounded-full blur-[50px] group-hover:bg-[#FDA400]/[0.10] group-hover:scale-125 transition-all duration-700" />
                                <span className="absolute top-6 right-8 text-[120px] font-black text-[#1C2B6E]/[0.03] leading-none select-none group-hover:text-[#1C2B6E]/[0.06] transition-colors duration-500">
                                    {item.num}
                                </span>

                                <div className="p-8 md:p-10 h-full flex flex-col relative z-10">
                                    <div className="mb-6">
                                        <div className="text-5xl md:text-6xl font-black text-[#FDA400] leading-none mb-1">
                                            {item.stat}
                                        </div>
                                        <div className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold">
                                            {item.statLabel}
                                        </div>
                                    </div>

                                    <h3 className="text-2xl font-black text-[#1C2B6E] tracking-tight mb-3">
                                        {item.title}
                                    </h3>
                                    <p className="text-slate-500 text-sm leading-relaxed font-light mb-6">
                                        {item.description}
                                    </p>

                                    <div className="flex flex-wrap gap-2 mt-auto pt-6 border-t border-slate-100">
                                        {item.painPoints.map((point, i) => (
                                            <span
                                                key={i}
                                                className="px-3 py-1.5 bg-[#1C2B6E]/[0.04] text-[#1C2B6E]/60 text-xs font-medium rounded-full border border-[#1C2B6E]/[0.06] hover:bg-[#FDA400]/10 hover:border-[#FDA400]/30 hover:text-[#FDA400] transition-all duration-300 cursor-default"
                                            >
                                                {point}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Cloud-First Approach */}
            <section className="py-24 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.15),transparent_70%)]" />
                <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.03] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
                        <div>
                            <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                                Our Methodology
                            </p>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1] mb-6">
                                Cloud-First{" "}
                                <span className="text-[#FDA400]">Engineering</span>{" "}
                                Approach
                            </h2>
                            <p className="text-slate-400 text-lg font-light leading-relaxed mb-10">
                                We architect cloud foundations that are scalable, secure, and cost-efficient from day one - built for the way modern enterprises operate.
                            </p>

                            <div className="space-y-8">
                                {approachSteps.map((step) => (
                                    <div key={step.num} className="flex gap-5 group">
                                        <div className="w-12 h-12 bg-[#FDA400]/10 border border-[#FDA400]/20 rounded-full flex items-center justify-center flex-shrink-0">
                                            <span className="text-sm font-black text-[#FDA400]">{step.num}</span>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-black text-white tracking-tight mb-2">{step.title}</h3>
                                            <p className="text-slate-400 text-sm font-light leading-relaxed">{step.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Cloud Architecture Visualization */}
                        <div className="relative">
                            <div className="absolute inset-0 bg-[#FDA400]/[0.03] rounded-3xl blur-[60px]" />
                            <div className="relative bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-3xl p-8 md:p-10">
                                <div className="flex items-center justify-between mb-8 border-b border-white/10 pb-4">
                                    <div className="text-xs text-slate-400 uppercase tracking-[0.2em] font-bold">Cloud Infrastructure Health</div>
                                    <div className="flex items-center gap-2">
                                        <span className="relative flex h-2 w-2">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FDA400] opacity-40" />
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FDA400]" />
                                        </span>
                                        <span className="text-[#FDA400] text-xs font-bold">All Systems Go</span>
                                    </div>
                                </div>
                                <div className="space-y-6">
                                    {[
                                        { label: "Uptime SLA", value: "99.99%", width: "99%", color: "bg-[#FDA400]" },
                                        { label: "Auto-Scaling Efficiency", value: "94%", width: "94%", color: "bg-[#1C2B6E]" },
                                        { label: "Cost Optimization", value: "38%", width: "38%", color: "bg-[#FDA400]/70" },
                                        { label: "Security Posture", value: "97%", width: "97%", color: "bg-[#FDA400]" },
                                    ].map((metric, i) => (
                                        <div key={i}>
                                            <div className="flex justify-between text-xs mb-2">
                                                <span className="text-slate-400 font-medium">{metric.label}</span>
                                                <span className="text-white font-bold">{metric.value}</span>
                                            </div>
                                            <div className="h-2 bg-white/[0.06] rounded-full overflow-hidden">
                                                <div className={`h-full ${metric.color} rounded-full`} style={{ width: metric.width }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-8 pt-6 border-t border-white/10 grid grid-cols-3 gap-4">
                                    {[
                                        { value: "12", label: "Regions" },
                                        { value: "340+", label: "Microservices" },
                                        { value: "< 5ms", label: "Latency P99" },
                                    ].map((stat, i) => (
                                        <div key={i} className="text-center">
                                            <div className="text-lg font-black text-[#FDA400]">{stat.value}</div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold">{stat.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What We Deliver - Scrolling Marquee */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.15]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
                <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Deliverables
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            What We{" "}
                            <span className="text-[#FDA400]">Deliver</span>
                        </h2>
                        <p className="text-slate-500 text-lg mt-6 max-w-2xl mx-auto font-light leading-relaxed">
                            End-to-end cloud and infrastructure services built to modernize, secure, and scale your enterprise.
                        </p>
                    </div>
                </div>

                <div className="relative z-10 space-y-4">
                    <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white via-white/80 to-transparent z-20 pointer-events-none" />
                    <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white via-white/80 to-transparent z-20 pointer-events-none" />

                    {deliverableRows.map((row, rowIndex) => (
                        <div key={rowIndex} className="overflow-hidden">
                            <div
                                className="flex gap-4 w-max"
                                style={{
                                    animation: `${rowIndex % 2 === 0 ? "scrollLeft" : "scrollRight"} ${30 + rowIndex * 5}s linear infinite`,
                                }}
                            >
                                {[...row, ...row].map((item, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center gap-3 px-6 py-4 bg-white rounded-2xl border border-[#1C2B6E]/[0.06] shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:border-[#FDA400]/30 hover:shadow-[0_12px_40px_rgba(28,43,110,0.1)] transition-all duration-300 group cursor-default flex-shrink-0"
                                    >
                                        <span className="relative flex h-2 w-2 flex-shrink-0">
                                            <span className="absolute inline-flex h-full w-full rounded-full bg-[#FDA400] opacity-30 group-hover:animate-ping" />
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FDA400]" />
                                        </span>
                                        <span className="text-[#1C2B6E] font-bold text-sm whitespace-nowrap group-hover:text-[#FDA400] transition-colors duration-300">
                                            {item}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Enterprise Ready + Engagement Model */}
            <section className="py-24 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.15),transparent_70%)]" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.03] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    {/* Enterprise Ready */}
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Built for Scale
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1]">
                            Enterprise Ready{" "}
                            <span className="text-[#FDA400]">by Design</span>
                        </h2>
                        <p className="text-slate-400 text-lg mt-6 max-w-2xl mx-auto font-light leading-relaxed">
                            Architected for complex enterprise ecosystems with regional, regulatory, and performance requirements.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-24">
                        {[
                            { num: "01", title: "Hybrid & Multi-Cloud", desc: "Native support for AWS, Azure, GCP, and on-premise infrastructure with unified governance.", tags: ["AWS", "Azure", "GCP", "On-Prem"] },
                            { num: "02", title: "Security & Compliance Built-In", desc: "Zero-trust principles, secure networking, and compliance-aligned controls from the ground up.", tags: ["SOC2", "GDPR", "HIPAA", "ISO 27001"] },
                        ].map((item) => (
                            <div
                                key={item.num}
                                className="relative bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-3xl p-8 md:p-10 hover:bg-white/[0.07] hover:border-[#FDA400]/20 transition-all duration-500 group"
                            >
                                <span className="absolute top-4 right-6 text-7xl font-black text-white/[0.03] leading-none select-none group-hover:text-white/[0.06] transition-colors duration-500">
                                    {item.num}
                                </span>
                                <div className="relative z-10">
                                    <div className="text-xs font-bold text-[#FDA400] uppercase tracking-[0.2em] mb-4">
                                        {item.num}
                                    </div>
                                    <h3 className="text-2xl font-black text-white tracking-tight mb-3">{item.title}</h3>
                                    <p className="text-slate-400 text-sm font-light leading-relaxed mb-6">{item.desc}</p>
                                    <div className="flex flex-wrap gap-2 pt-5 border-t border-white/[0.06]">
                                        {item.tags.map((tag, i) => (
                                            <span key={i} className="px-3 py-1.5 bg-white/[0.04] text-slate-400 text-xs font-medium rounded-full border border-white/[0.08] hover:bg-[#FDA400]/10 hover:border-[#FDA400]/30 hover:text-[#FDA400] transition-all duration-300 cursor-default">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Engagement Model */}
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Our Process
                        </p>
                        <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight leading-[1.1]">
                            Strategy-Led{" "}
                            <span className="text-[#FDA400]">Engagement</span>
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 relative max-w-5xl mx-auto">
                        <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-[2px] bg-gradient-to-r from-[#FDA400]/40 via-[#1C2B6E]/30 to-[#FDA400]/40 z-0" />

                        {[
                            { num: "01", title: "Assess & Architect", desc: "Evaluate current infrastructure and define a future-ready cloud blueprint." },
                            { num: "02", title: "Build & Modernize", desc: "Execute migrations and modernization with strong governance and minimal disruption." },
                            { num: "03", title: "Optimize & Scale", desc: "Continuously improve cost, performance, and security using intelligent automation." },
                        ].map((step) => (
                            <div key={step.num} className="relative z-10 text-center">
                                <div className="w-28 h-28 mx-auto bg-[#0f1629] border-2 border-[#FDA400]/30 rounded-full flex items-center justify-center mb-8 shadow-[0_0_30px_rgba(253,164,0,0.1)]">
                                    <span className="text-3xl font-black text-[#FDA400]">{step.num}</span>
                                </div>
                                <h3 className="text-xl font-black text-white tracking-tight mb-3">
                                    {step.title}
                                </h3>
                                <p className="text-slate-400 text-sm font-light leading-relaxed max-w-xs mx-auto">
                                    {step.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <PageFAQ
                faqs={cloudInfrastructureFAQs}
                title="Cloud Infrastructure FAQs"
                subtitle="Common questions about cloud migration, architecture, and optimization"
                variant="muted"
            />

            {/* CTA */}
            <section className="py-28 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.2),transparent_70%)]" />
                <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-[#FDA400]/[0.04] rounded-full blur-[150px]" />

                <div className="section-container relative z-10 text-center">
                    <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                        Get Started
                    </p>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1] mb-6">
                        Build a Cloud Foundation That{" "}
                        <span className="text-[#FDA400]">Scales With Your Business</span>
                    </h2>
                    <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto font-light leading-relaxed">
                        Partner with RSA Tech Group to modernize infrastructure with confidence and clarity.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-[#1C2B6E] bg-[#FDA400] rounded-full hover:bg-[#f5b800] transition-all duration-300 shadow-[0_8px_30px_rgba(253,164,0,0.3)] hover:shadow-[0_12px_40px_rgba(253,164,0,0.4)] hover:-translate-y-0.5"
                    >
                        Talk to a Cloud Infrastructure Expert <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    )
}

export default CloudInfrastructure
